import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import { LeapFeeTaskCodeAccountingItem } from '@state/matter/accounting/accounting.models';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { Observable } from 'rxjs';
import { StatementOfAccountsDispatch } from '@state/matter/statementOfAccounts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchableDropdownItems } from 'src/app/shared/atomic/atoms/searchable-dropdown/searchable-dropdown.types';
import {
	LeapCostRecoveryItem,
	LeapFeeTaskCodeItem,
} from '@state/matter/accounting/accounting.models';
import { MatterStatementOfAccount } from '@models/statementOfAccount';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { CostRecoveryTaskCode, FeeTaskCode } from '@models/leap';
import { LeapForBusiness } from '@models/businesses';
import { getLeapCodeLabelDescription } from '@core/util/accounting/leap';

@UntilDestroy()
@Component({
	selector: 'app-leap-accounting-items-table',
	templateUrl: './leap-accounting-items-table.component.html',
	styleUrls: ['./leap-accounting-items-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LeapAccountingItemsTableComponent implements OnInit {
	public displayedColumns: string[] = ['title', 'amount', 'activityCode'];

	public items: LeapFeeTaskCodeItem[] | LeapCostRecoveryItem[] = [];
	public costRecoveryItems: LeapCostRecoveryItem[] = [];

	@Select(BusinessState.getLeapTaskCodes)
	activityCodes$: Observable<LeapForBusiness>;
	@ViewSelectSnapshot(BusinessState.getLeapTaskCodes)
	activityCodes: LeapForBusiness;
	public activityCodesDropdownOptions: SearchableDropdownItems[] = [];

	@Input('items') set _items(
		items: LeapFeeTaskCodeItem[] | LeapCostRecoveryItem[],
	) {
		this.items = items;
		if (this.feeMode) {
			this.total = (items as LeapFeeTaskCodeItem[]).reduce(
				(acc, item: LeapFeeTaskCodeItem) => acc + item.accountingItem.amount,
				0,
			);
		} else {
			this.total = (items as LeapCostRecoveryItem[]).reduce(
				(acc, item: LeapCostRecoveryItem) => acc + item.accountingItem.amount,
				0,
			);
		}
	}

	@Input() title = '';

	@Input() hst: number = null;
	@Input() hstTotal: number = null;
	@Input() activityCodeColumnTitle = 'Activity Code';
	@Input() feeMode = true;

	public total = 0;

	constructor(
		private statementOfAccountDispatch: StatementOfAccountsDispatch,
		private analyticsService: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.activityCodes$
			.pipe(untilDestroyed(this))
			.subscribe((activityCodes: LeapForBusiness) => {
				if (this.feeMode) {
					this.activityCodesDropdownOptions = activityCodes.feeTaskCodes.map(
						(activityCode: FeeTaskCode) => {
							return {
								label: getLeapCodeLabelDescription(activityCode),
								value: activityCode,
							};
						},
					);
				} else {
					this.activityCodesDropdownOptions =
						activityCodes.costRecoveryTaskCodes.map(
							(activityCode: CostRecoveryTaskCode) => {
								return {
									label: getLeapCodeLabelDescription(activityCode),
									value: activityCode,
								};
							},
						);
				}
			});
	}

	onActivityCodeChange(
		event: FeeTaskCode | CostRecoveryTaskCode,
		item: LeapFeeTaskCodeItem | LeapCostRecoveryItem,
	) {
		this.analyticsService.logEvent(
			AnalyticsEvent.LEAP_FILE_ADDED_ACTIVITY_CODE,
		);

		const result: MatterStatementOfAccount = { ...item.statement };
		if (event) {
			if (this.feeMode) {
				result.leap = { feeTaskCode: event as FeeTaskCode };
			} else {
				result.leap = { costRecoveryTaskCode: event as CostRecoveryTaskCode };
			}
		} else {
			result.leap = { feeTaskCode: null, costRecoveryTaskCode: null };
		}

		this.statementOfAccountDispatch.SetStatement(item.statement.id, {
			...result,
		});
	}

	getBackupValue(code: FeeTaskCode | CostRecoveryTaskCode): string {
		return getLeapCodeLabelDescription(code);
	}

	trackByFn(index: number, item: LeapFeeTaskCodeAccountingItem) {
		return item.statementId;
	}

	protected readonly location = location;
}
